import { useState, useCallback, useRef } from 'react';
import { api } from '../../helpers/api';

interface ValidationWarning {
	type: string;
	severity: 'error' | 'warning';
	message: string;
	resourceId?: number;
}

interface ValidationParams {
	serviceId: number;
	startDatetime: string;
	endDatetime?: string;
	resourceIds?: number[];
	quantity?: number;
	entryId?: number;
	excludeBookingId?: number;
}

interface UseBookingValidationReturn {
	warnings: ValidationWarning[];
	isValidating: boolean;
	validateBooking: (params: ValidationParams) => void;
	clearWarnings: () => void;
}

/**
 * Hook for validating booking times in entry edit mode.
 *
 * Uses the admin validation endpoint to check for availability issues,
 * capacity problems, and other constraints. Returns warnings instead of
 * blocking, allowing admins to override.
 */
export function useBookingValidation(): UseBookingValidationReturn {
	const [warnings, setWarnings] = useState<ValidationWarning[]>([]);
	const [isValidating, setIsValidating] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const clearWarnings = useCallback(() => {
		setWarnings([]);
	}, []);

	const validateBookingImmediate = useCallback(
		async (params: ValidationParams) => {
			setIsValidating(true);

			try {
				const data = await api.post(
					'/gp-bookings/v1/admin/bookings/validate',
					{
						data: params,
					}
				);

				setWarnings(data.warnings || []);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Booking validation error:', error);
				// Don't set warnings on network errors - just log them
				setWarnings([]);
			} finally {
				setIsValidating(false);
			}
		},
		[]
	);

	// Debounce validation calls by 500ms
	const validateBooking = useCallback(
		(params: ValidationParams) => {
			// Clear any pending validation
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Schedule new validation
			timeoutRef.current = setTimeout(() => {
				validateBookingImmediate(params);
			}, 500);
		},
		[validateBookingImmediate]
	);

	return {
		warnings,
		isValidating,
		validateBooking,
		clearWarnings,
	};
}
